import Money from "@/components/Money";
import {
  ShieldCheck, Snowflake, Disc, AirVent, Lock, Zap, Sparkles, Wind,
  Music, Smartphone, Camera, Gauge, ParkingCircle, CircleSlash2, X,
} from "lucide-react";

/**
 * Insurance plan list (Iter 67o).
 *
 * Coverage chips now carry a coloured icon + tinted background so renters
 * can scan a plan's perks at a glance. A keyword matcher picks the most
 * relevant lucide glyph + palette — falls back to a neutral indigo chip when
 * the host writes something we don't recognise.
 */
const CHIP_PALETTES = {
  cdw:        { icon: ShieldCheck,     bg: "bg-emerald-50",  fg: "text-emerald-700",  ring: "border-emerald-200" },
  ac:         { icon: Snowflake,       bg: "bg-cyan-50",     fg: "text-cyan-700",     ring: "border-cyan-200" },
  abs:        { icon: Disc,            bg: "bg-indigo-50",   fg: "text-indigo-700",   ring: "border-indigo-200" },
  airbag:     { icon: AirVent,         bg: "bg-rose-50",     fg: "text-rose-700",     ring: "border-rose-200" },
  lock:       { icon: Lock,            bg: "bg-amber-50",    fg: "text-amber-700",    ring: "border-amber-200" },
  power:      { icon: Zap,             bg: "bg-yellow-50",   fg: "text-yellow-700",   ring: "border-yellow-200" },
  parking:    { icon: ParkingCircle,   bg: "bg-fuchsia-50",  fg: "text-fuchsia-700",  ring: "border-fuchsia-200" },
  cruise:     { icon: Gauge,           bg: "bg-sky-50",      fg: "text-sky-700",      ring: "border-sky-200" },
  bluetooth:  { icon: Smartphone,      bg: "bg-blue-50",     fg: "text-blue-700",     ring: "border-blue-200" },
  music:      { icon: Music,           bg: "bg-violet-50",   fg: "text-violet-700",   ring: "border-violet-200" },
  camera:     { icon: Camera,          bg: "bg-slate-50",    fg: "text-slate-700",    ring: "border-slate-200" },
  wind:       { icon: Wind,            bg: "bg-teal-50",     fg: "text-teal-700",     ring: "border-teal-200" },
  theft:      { icon: CircleSlash2,    bg: "bg-orange-50",   fg: "text-orange-700",   ring: "border-orange-200" },
  default:    { icon: Sparkles,        bg: "bg-indigo-50",   fg: "text-indigo-700",   ring: "border-indigo-200" },
};

// Tiny keyword router: pick palette based on what the chip text says.
function paletteFor(chip) {
  const s = String(chip || "").toLowerCase();
  if (/cdw|collision|liability/.test(s)) return CHIP_PALETTES.cdw;
  if (/a\/c|air\s*condition/.test(s))    return CHIP_PALETTES.ac;
  if (/abs/.test(s))                      return CHIP_PALETTES.abs;
  if (/airbag|air\s*bag/.test(s))         return CHIP_PALETTES.airbag;
  if (/central\s*lock|lock|key/.test(s))  return CHIP_PALETTES.lock;
  if (/electric\s*window|power\s*window|window/.test(s)) return CHIP_PALETTES.power;
  if (/park|sensor/.test(s))              return CHIP_PALETTES.parking;
  if (/cruise/.test(s))                   return CHIP_PALETTES.cruise;
  if (/bluetooth|carplay|android\s*auto/.test(s)) return CHIP_PALETTES.bluetooth;
  if (/music|radio|stereo/.test(s))       return CHIP_PALETTES.music;
  if (/camera|backup|reverse/.test(s))    return CHIP_PALETTES.camera;
  if (/heated|climate/.test(s))           return CHIP_PALETTES.wind;
  if (/theft|alarm/.test(s))              return CHIP_PALETTES.theft;
  return CHIP_PALETTES.default;
}

export default function CarInsurancePlansList({ plans = [] }) {
  if (plans.length === 0) return null;
  return (
    <div className="py-8 border-b border-border" data-testid="car-insurance-section">
      <h3 className="font-display text-xl font-medium mb-1 flex items-center gap-2">
        <span className="h-8 w-8 rounded-lg bg-emerald-50 ring-1 ring-emerald-100 flex items-center justify-center">
          <ShieldCheck className="h-4 w-4 text-emerald-600" />
        </span>
        Insurance plans
      </h3>
      <p className="text-sm text-neutral-500 mb-5">Optional add-on coverage. Pick one in the booking widget — or skip it entirely.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {plans.map((p) => (
          <div
            key={p.id}
            className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50/60 via-white to-teal-50/40 p-4"
          >
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <div className="font-semibold text-neutral-900">{p.name}</div>
              <div className="text-sm font-semibold"><Money amount={p.price_per_day} /><span className="text-neutral-500 font-normal">/day</span></div>
            </div>
            {p.description && <div className="text-sm text-neutral-600 mb-3">{p.description}</div>}
            {(p.coverage || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {p.coverage.map((c) => {
                  const pal = paletteFor(c);
                  return (
                    <span
                      key={c}
                      className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border ${pal.bg} ${pal.fg} ${pal.ring}`}
                    >
                      <pal.icon className="h-3 w-3" />
                      {c}
                    </span>
                  );
                })}
              </div>
            )}
            {/* Iter 67bu — Exclusions panel. Red-tinted block lists what's NOT
                covered so renters can't miss the gaps before booking. Hidden
                when the plan has no exclusions (back-compat with legacy plans). */}
            {(p.exclusions || []).length > 0 && (
              <div
                className="mt-3 rounded-xl bg-rose-50 border border-rose-200 px-3 py-2"
                data-testid={`car-insurance-plan-${p.id}-exclusions`}
              >
                <div className="text-[11px] uppercase tracking-widest font-semibold text-rose-700 mb-1.5 flex items-center gap-1">
                  <X className="h-3 w-3" /> Not included
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-rose-900">
                  {p.exclusions.map((c, i) => (
                    <span key={`x-${i}`} className="inline-flex items-start gap-1">
                      <X className="h-3 w-3 mt-0.5 shrink-0 text-rose-700" />
                      <span>{c}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
